import { useState } from 'react'
import { Kopfleiste, Seitenkopf } from './Kopfleiste.js'
import { Fuss } from './Fuss.js'
import { useSeitenleben } from './bewegung.js'
import { PAKETE, TESTPHASE_TAGE } from '@raporto/shared'

/**
 * Die Fragenseite.
 *
 * Sie ist nicht die Kurzfassung der AGB. Die AGB sagen, was gilt; diese Seite
 * sagt, was jemand wissen will, bevor er sich anmeldet – in den Worten, in
 * denen er die Frage stellen würde, und mit einer Antwort, die er auch dann
 * versteht, wenn ihn nur der eine Punkt interessiert.
 *
 * Zwei Regeln beim Schreiben:
 *
 *   Jede Antwort steht für sich. Niemand liest diese Seite von oben nach
 *   unten; jeder springt zu seiner Frage. «Wie oben erwähnt» ist deshalb
 *   hier immer ein Fehler.
 *
 *   Keine Antwort verspricht etwas, was es nicht gibt. Das ist auf einer
 *   Verkaufsseite die schwerere Regel, denn genau hier ist die Versuchung am
 *   grössten. Wo Raporto etwas NICHT tut – Buchhaltung, Lohn, Steuern –,
 *   steht es hier ausdrücklich. Ein Betrieb, der es nach zwei Wochen selbst
 *   merkt, kündigt; einer, der es vorher liest, bleibt oder kommt gar nicht
 *   erst – beides ist besser.
 */

/* Die Pakete beim Namen, den sie überall tragen. */
const PAKETNAMEN = PAKETE.map((p) => p.name).join(', ')

type Frage = { frage: string; antwort: string }

/*
  Eine flache Liste, keine Gruppen.

  Dreizehn Fragen sind kurz genug, um sie zu überfliegen; vier Überschriften
  darüber zwingen den Leser, zuerst die Gruppe zu wählen und erst dann die
  Frage. Die Reihenfolge trägt die Gliederung: was es ist, was es tut, was es
  kostet, wo die Daten liegen.
*/
const FRAGEN: Frage[] = [
  {
    frage: 'Was ist Raporto?',
    antwort:
      'Eine Schweizer Software für Betriebe, die auf Baustellen und beim Kunden arbeiten. ' +
      'Rapporte, Offerten, Rechnungen und Zahlungen liegen in einem System und in einem ' +
      'Datensatz: Was der Monteur am Handy erfasst, steht in der Offerte und auf der ' +
      'Rechnung, ohne dass es jemand ein zweites Mal tippt.'
  },
  {
    frage: 'Für wen ist Raporto gedacht?',
    antwort:
      'Für kleine und mittlere Betriebe vom Einzelunternehmer bis etwa zwanzig Personen: ' +
      'Bodenleger, Maler, Gipser, Sanitär, Elektro, Gartenbau, Reinigung, Montage. Wer ' +
      'Stunden und Material auf der Baustelle erfasst und daraus Rechnungen macht, ist ' +
      'hier richtig. Wer Serienfertigung, Lagerhaltung oder eine Werkstattplanung braucht, ' +
      'ist es nicht.'
  },
  {
    frage: 'Ist Raporto eine Buchhaltung?',
    antwort:
      'Nein, und das ist keine Untertreibung. Raporto schreibt Rechnungen und erfasst ' +
      'Zahlungseingänge. Es führt kein Hauptbuch, macht keine Mehrwertsteuerabrechnung, ' +
      'keinen Lohn, keinen Jahresabschluss und keine Bilanz. Dein Treuhänder bleibt dein ' +
      'Treuhänder – er bekommt von Raporto sauberere Unterlagen, nicht weniger Arbeit auf ' +
      'deiner Seite als Rechnung.'
  },
  {
    frage: 'Was ist eine Express-Offerte?',
    antwort:
      'Eine Offerte in einem Bildschirm. Kunde wählen, Positionen aus dem Leistungskatalog ' +
      'antippen, Menge eintragen, fertig – Nummer, Gültigkeit und Mehrwertsteuer setzt ' +
      'Raporto selbst. Gedacht für den Moment beim Kunden, in dem eine Offerte sonst auf ' +
      '«mache ich heute Abend» verschoben wird und dann zwei Tage liegen bleibt.'
  },
  {
    frage: 'Was ist eine Express-Rechnung?',
    antwort:
      'Dasselbe für die Rechnung: Positionen antippen, abschliessen, PDF mit Schweizer ' +
      'QR-Zahlteil. Wenn ein Rapport zum Auftrag vorliegt, übernimmt die Rechnung Stunden ' +
      'und Material daraus, statt dass jemand die Zettel abtippt.'
  },
  {
    frage: 'Kann ich Rapporte unterwegs erfassen?',
    antwort:
      'Ja, dafür ist es gebaut. Stunden, Material und Fotos am Handy, direkt auf der ' +
      'Baustelle. Monteure können auf Wunsch ohne Preise arbeiten – sie sehen dann ihre ' +
      'Aufträge und ihre Stunden, aber keine Beträge.'
  },
  {
    frage: 'Kann ich Raporto im Browser und am Handy nutzen?',
    antwort:
      'Ja. Es gibt nichts zu installieren und keine App aus einem Store. Raporto läuft im ' +
      'Browser – am Bürorechner, am Tablet und am Handy. Angemeldet wird einmal, danach ' +
      'ist es auf jedem Gerät derselbe Stand.'
  },
  {
    frage: 'Kann ich Daten für den Treuhänder exportieren?',
    antwort:
      'Ja. Kunden, Rechnungen und Zahlungen als Datei, die sich in einer Tabelle öffnen ' +
      'lässt, dazu die abgeschlossenen Belege als PDF. Weitere Bestände geben wir auf ' +
      'Anfrage heraus. Der Export gehört dir und steht während der ganzen Vertragsdauer ' +
      'zur Verfügung, nicht erst beim Aufhören.'
  },
  {
    frage: 'Welche Pakete gibt es?',
    antwort:
      `Drei: ${PAKETNAMEN}. Sie unterscheiden sich in der Zahl der Benutzer und im ` +
      'Speicher für Belege und Fotos; die Vergleichstabelle auf der Preisseite zeigt Zeile ' +
      'für Zeile, worin sonst noch. Wechseln geht in den Einstellungen und gilt sofort.'
  },
  {
    frage: `Was bedeutet ${TESTPHASE_TAGE} Tage kostenlos testen?`,
    antwort:
      `${TESTPHASE_TAGE} Tage voller Funktionsumfang, ohne Einschränkung und ohne ` +
      'Einrichtungsgebühr. Du arbeitest mit deinen eigenen Daten, nicht in einer ' +
      'Demoumgebung – was du in der Testphase erfasst, ist danach da.'
  },
  {
    frage: 'Brauche ich eine Zahlungsart?',
    antwort:
      'Ja. Beim Anlegen des Betriebs wird eine Zahlungsart hinterlegt. Abgebucht wird ' +
      `nichts, solange die Testphase läuft – die Abrechnung beginnt erst nach Ablauf der ` +
      `${TESTPHASE_TAGE} Tage. Kündigst du vorher, entsteht keine Rechnung.`
  },
  {
    frage: 'Wie lange bin ich gebunden?',
    antwort:
      'Gar nicht. Keine Mindestlaufzeit, kein Jahresvertrag, der stillschweigend ' +
      'weiterläuft. Gekündigt wird auf das Ende des laufenden Abrechnungsmonats, ohne ' +
      'Frist und ohne Begründung.'
  },
  {
    frage: 'Wo werden Daten gespeichert?',
    antwort:
      'Die Datenbank steht in der Region Zürich. Die Anwendung selbst läuft bei einem ' +
      'anderen Dienstleister, Belege und Baustellenfotos liegen bei einem Schweizer ' +
      'Anbieter. Datenhaltung und Verarbeitung finden deshalb in der Schweiz und/oder der ' +
      'EU statt, je nach Dienstleister. Welcher Anbieter was macht, steht namentlich in ' +
      'der Datenschutzerklärung – dort steht auch, was wir nicht ausschliessen können.'
  },
  {
    frage: 'Gibt es Support?',
    antwort:
      'Ja, und er kostet nichts zusätzlich. Fragen zur Bedienung, zur Einrichtung und zum ' +
      'Übernehmen der Stammdaten sind im Paket enthalten. Geschrieben wird an ' +
      'info@raporto.ch; eine Telefonnummer gibt es noch nicht, und wir stellen hier keine ' +
      'hin, die dann niemand abnimmt.'
  }
]

export function Faq({
  angemeldet,
  aufStart,
  aufPreise,
  aufPfad,
  aufAnmelden,
  aufStarten
}: {
  angemeldet: boolean
  aufStart: () => void
  aufPreise: () => void
  aufPfad: (pfad: string) => void
  aufAnmelden: () => void
  aufStarten: () => void
}): JSX.Element {
  /*
    Genau eine Frage ist offen.

    Alle gleichzeitig offen ist eine Textwand, alle zu ist eine Liste ohne
    Inhalt. Die erste steht offen, damit sichtbar ist, dass hier überhaupt
    Antworten stehen und nicht nur Überschriften.
  */
  const [offen, setOffen] = useState<string | null>(FRAGEN[0].frage)

  const leben = useSeitenleben<HTMLDivElement>()

  return (
    <div ref={leben} className="rp-glasseite">
      <Kopfleiste
        hier="faq"
        angemeldet={angemeldet}
        aufStart={aufStart}
        aufPfad={aufPfad}
        aufAnmelden={aufAnmelden}
        aufStarten={aufStarten}
      />

      <Seitenkopf
        augenbraue="Antworten auf einen Blick"
        titel="Alles, was du wissen musst,"
        hell="bevor du startest."
        vorspann="Klare, kurze Antworten auf die häufigsten Fragen. Tippe auf eine Frage, um sie zu öffnen."
      />

      <main className="rp-glas-mitte">
        <div className="rp-glas rp-glas-fragen">
          {FRAGEN.map((f) => {
            const auf = offen === f.frage
            return (
              <div className={auf ? 'rp-glas-frage offen' : 'rp-glas-frage'} key={f.frage}>
                <button
                  type="button"
                  className="rp-glas-fragekopf"
                  aria-expanded={auf}
                  onClick={() => setOffen(auf ? null : f.frage)}
                >
                  <span>{f.frage}</span>
                  <span className="rp-glas-zeichen" aria-hidden="true" />
                </button>
                {auf && <p className="rp-glas-antwort">{f.antwort}</p>}
              </div>
            )
          })}
        </div>

        {/*
          Zwei Wege am Ende, nicht einer.

          Wer bis hierher gelesen hat, gehört in eine von zwei Gruppen: Frage
          beantwortet – dann zu den Paketen. Frage nicht dabei – dann zum
          Kontakt. Ein einziger Knopf schickt die Hälfte in die falsche
          Richtung.
        */}
        <div className="rp-glas rp-glas-aufruf">
          <p className="rp-augenbraue-punkt">Noch etwas offen?</p>
          <h2>Deine Frage steht nicht dabei?</h2>
          <p>
            Dann fehlt sie auf dieser Seite, und das ist unser Fehler. Schreib uns, und die
            Antwort steht beim nächsten Mal hier.
          </p>
          <div className="rp-glas-knoepfe">
            <button
              type="button"
              className="rp-kl-start"
              id="btn-faq-pakete"
              onClick={aufPreise}
            >
              Pakete ansehen
              <span aria-hidden="true">→</span>
            </button>
            <button
              type="button"
              className="rp-kl-anmelden"
              id="btn-faq-kontakt"
              onClick={() => aufPfad('/kontakt')}
            >
              Kontakt aufnehmen
            </button>
          </div>
        </div>
      </main>

      <Fuss aufPfad={aufPfad} />
    </div>
  )
}
